import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, ExternalLink, X, BadgeCheck } from 'lucide-react';
import api from '../../lib/api';
import CenterForm from '../../components/CenterForm';
import { typeLabel } from '../../lib/centerOptions';
import { useToast } from '../../hooks/use-toast';

const STATUS_STYLE = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  inactive: 'bg-red-100 text-red-700',
};

const MyCenters = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | center | null

  const fetchMine = async () => {
    setLoading(true);
    try { const res = await api.get('/centers/mine'); setItems(res.data || []); }
    catch (e) { console.error('Failed to load centers:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchMine(); }, []);

  const remove = async (c) => {
    if (!window.confirm('Delete this listing?')) return;
    try { await api.delete(`/centers/${c.id}`); toast({ title: 'Deleted' }); fetchMine(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  if (editing) {
    return (
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif-display text-3xl text-burgundy">{editing === 'new' ? 'Register a Center' : 'Edit Center'}</h1>
          <button onClick={() => setEditing(null)} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
        </div>
        <CenterForm center={editing === 'new' ? null : editing} onDone={() => { setEditing(null); fetchMine(); }} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Community</div>
          <h1 className="font-serif-display text-4xl text-burgundy">My <span className="font-italic-display text-rose-italic">Centers</span></h1>
          <p className="mt-2 text-mahogany/80">Register your ashram, temple, commune or center. Submissions are reviewed by an admin before going live.</p>
        </div>
        <button onClick={() => setEditing('new')} className="pill-btn" data-testid="register-center-btn"><Plus className="w-4 h-4" /> Register Center</button>
      </div>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : items.length === 0 ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="my-centers-empty">No centers yet. Register your first one!</div>
        : (
          <ul className="space-y-3" data-testid="my-centers-list">
            {items.map((c) => (
              <li key={c.id} className="p-5 rounded-2xl border border-rose/50 bg-white/70 flex flex-wrap items-center justify-between gap-4" data-testid={`my-center-${c.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif-display text-xl text-burgundy">{c.name}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{typeLabel(c.type)}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[c.status] || ''}`}>{c.status}</span>
                    {c.is_verified && <BadgeCheck className="w-4 h-4 text-green-600" />}
                  </div>
                  <div className="text-sm text-mahogany/70 mt-1">{[c.location?.city, c.location?.country].filter(Boolean).join(', ') || '—'}</div>
                  {c.status === 'inactive' && c.rejection_reason && (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid={`my-center-feedback-${c.id}`}>
                      <span className="font-medium">Admin feedback:</span> {c.rejection_reason}
                      <span className="block text-red-500/80 mt-0.5">Edit this listing to address the feedback — saving will resubmit it for review.</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {c.status === 'active' && <IconBtn onClick={() => window.open(`/centers/${c.slug}`, '_blank')} title="Preview"><ExternalLink className="w-4 h-4" /></IconBtn>}
                  <IconBtn onClick={() => setEditing(c)} title="Edit"><Pencil className="w-4 h-4" /></IconBtn>
                  <IconBtn onClick={() => remove(c)} title="Delete" danger><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

const IconBtn = ({ children, danger, ...p }) => (
  <button {...p} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${danger ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-rose-soft/60 text-mahogany hover:bg-rose-soft'}`}>{children}</button>
);

export default MyCenters;
